import React, { useEffect } from 'react';
import type { CategorizeItem } from '../types';
import CategorizeRow from './CategorizeRow';
import CategorizeInlineNotice from './CategorizeInlineNotice';
import { useCategorizeActions } from './useCategorizeActions';
import { approveAllEventName, dismissAllEventName } from '../archive/constants';
import { GRID_TEMPLATE } from './constants';

interface Props {
  items: CategorizeItem[];
}

export default function CategorizeBody({ items }: Props): React.ReactElement {
  const { decoratedItems, notice, approve, dismiss, changeCategory, undo, approveAll, dismissAll } =
    useCategorizeActions(items);

  useEffect(() => {
    const approveHandler = () => approveAll();
    const dismissHandler = () => dismissAll();
    const approveEvent = approveAllEventName('categorize');
    const dismissEvent = dismissAllEventName('categorize');
    window.addEventListener(approveEvent, approveHandler);
    window.addEventListener(dismissEvent, dismissHandler);
    return () => {
      window.removeEventListener(approveEvent, approveHandler);
      window.removeEventListener(dismissEvent, dismissHandler);
    };
  }, [approveAll, dismissAll]);

  return (
    <>
      {notice && <CategorizeInlineNotice notice={notice} onUndo={undo} />}
      {decoratedItems.length === 0 ? (
        <div className="mt-5 bg-white border border-lineSoft rounded-lg px-6 py-12 text-center text-helper text-small">
          All suggestions reviewed. Nothing left to categorize right now.
        </div>
      ) : (
        <div className="mt-5">
          <div
            className="grid gap-4 px-1 py-2.5 border-y border-line text-[10.5px] font-extrabold uppercase tracking-wider text-helper"
            style={{ gridTemplateColumns: GRID_TEMPLATE }}
          >
            <div>Date</div>
            <div>Description</div>
            <div className="text-right">Amount</div>
            <div>Vela suggests</div>
            <div className="text-right">Action</div>
          </div>
          {decoratedItems.map((item) => (
            <CategorizeRow
              key={item.id}
              item={item}
              onApprove={() => approve(item.id)}
              onDismiss={() => dismiss(item.id)}
              onChangeCategory={(cid) => changeCategory(item.id, cid)}
            />
          ))}
        </div>
      )}
    </>
  );
}